/**
 * A redirect's medium on the Redirects page (Lee, 1 Nov 2026). Pure.
 *
 * The media a person may set: the page's copy of functions/utils/media.js (the list the server accepts), held to it by
 * a test. The GUESS is per link (campaign and type), from redirectPreview.js's copy of go's rule; the medium set by
 * hand is per campaign. qrStats sends `utmMedium` (the hand-set one, or the guess for the campaign's busiest type)
 * and `utmMediumOverride` (set by hand).
 */
import { deriveMedium } from './redirectPreview';

export const MEDIA = Object.freeze(['email', 'print', 'qr', 'social', 'cpc', 'video']);

/** What the row shows: set by hand or guessed; for a guess, each different medium its links guess, with its types. */
export function mediumOf(campaign) {
  const c = campaign || {};
  const stored = c.utmMediumOverride && c.utmMedium ? c.utmMedium : null;
  const types = Object.keys(c.scansByType || {});
  const byMedium = new Map();
  for (const t of (types.length ? types : ['default'])) {
    const m = deriveMedium(c.campaignId, t);
    if (!byMedium.has(m)) byMedium.set(m, []);
    byMedium.get(m).push(t);
  }
  const guesses = [...byMedium.entries()].map(([medium, ofTypes]) => ({ medium, types: ofTypes }));
  // Set by hand ONLY when the stored medium differs from today's guess for at least one of the campaign's links: the
  // old medium box stored the guess whenever a campaign was saved, and a saved guess is not a choice (the review of
  // PR 197, H1). The same rule as channels.js effectiveScanTags.
  const setByHand = Boolean(stored) && guesses.some((g) => g.medium !== stored);
  return {
    setByHand,
    value: setByHand ? stored : ((guesses[0] && guesses[0].medium) || c.utmMedium || null),
    savedGuess: Boolean(stored) && !setByHand,
    offList: setByHand && !MEDIA.includes(c.utmMedium),
    guesses,
  };
}

/** The chip's words. */
export function mediumWords(m) {
  if (m.setByHand) {
    const links = m.guesses.reduce((t, g) => t + g.types.length, 0);
    const differ = m.guesses.filter((g) => g.medium !== m.value).reduce((t, g) => t + g.types.length, 0);
    const some = differ < links ? ` for ${differ} of ${links} links` : '';
    return m.offList ? `${m.value} · set by hand${some}, not on the list` : `${m.value} · set by hand${some}`;
  }
  if (m.guesses.length > 1) return `guessed: ${m.guesses.map((g) => `${g.medium} (${g.types.join(', ')})`).join(', ')}`;
  return `guessed: ${m.value}`;
}

/** Which rows the filter keeps. */
export const MEDIUM_FILTERS = Object.freeze([
  { value: 'all', label: 'All media' },
  { value: 'guessed', label: 'Guessed' },
  { value: 'set', label: 'Set by hand' },
]);
export function keptByMediumFilter(campaign, filter) {
  if (filter === 'guessed') return !mediumOf(campaign).setByHand;
  if (filter === 'set') return mediumOf(campaign).setByHand;
  return true;
}

export const MEDIUM_HELP = 'The medium is the utm_medium a redirect sends to the site, which Analytics files it by. A guessed one comes from the link\'s type (go\'s own rule); one set by hand applies to every link of the campaign. "Back to the guess" clears it.';

/** Said while the filter shows media set by hand (the review of PR 196, LOW-5). */
export const SET_BY_HAND_NOTE = 'A medium counts as set by hand only when it differs from the guess: the old medium box stored the guess whenever a campaign was saved, and those are shown as guessed.';
